import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { Pagination } from '../../components/ui/Pagination';
import { MobileFilters } from '../../components/ui/MobileFilters';
import { OrdinaFiltra, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina } from '../../components/ui/OrdinaFiltra';
import { useUrlState } from '../../hooks/useUrlState';
import { fmtEur, fmtDate } from '../../lib/utils';
import { accountsQuery, incassiQuery, usciteQuery, plurale, SOURCE_TO_MATCH, type MatchKind, type UscitaSource } from './adminApi';
import {
  COLLEGATO_LABEL, USCITA_LABEL, meseInParole, movimentiQuery, useBancaActions,
  type MovimentoConto,
} from './banca/bancaApi';
import { metodoDaOperazione } from './fatture/fattureApi';

/**
 * Banca › Movimenti del conto — l'estratto conto di Qonto, un movimento per riga.
 *
 *   filtri   conto · periodo · tutti / solo entrate / solo uscite ·
 *            "Solo movimenti senza documento" · cerca
 *   tabella  Data · Movimento · Conto · Importo · Documento collegato · Collega / Scollega
 *
 * "Documento collegato" dice il TIPO di documento (Fattura di un cliente, Fattura di un
 * fornitore, Tassa, Voce a mano) e, sotto, QUALE ("2026/161 di New Omnia").
 * I pagamenti in busta paga mostrano Stipendio / Rimborso km / TFR: a chi non ha
 * il livello ADMIN sull'Amministrazione l'API li manda senza nome né importo.
 *
 * Collegare = registrare: l'API segna la voce incassata o pagata alla data del
 * movimento. Una spesa fissa non ha ancora la sua voce: la si paga con il
 * movimento, ed è quello il collegamento.
 *
 * Stato in URL: acc · from · to · side · unmatched · q (le stesse chiavi di
 * prima: i link salvati continuano a funzionare).
 */
const PAGE_SIZE = 25;

/**
 * Ordina e filtra (24/9): la lista è paginata, ordina l'API (`sort`). Entrate /
 * uscite e "senza documento" erano pill in barra: ora stanno in Filtra per,
 * con le STESSE chiavi nell'URL (side, unmatched), così i link di Spese da
 * pagare ("Scegli il movimento") continuano ad aprire la lista giusta.
 */
const ORDINI: OpzioneOrdina[] = [
  { key: 'recenti', label: 'Movimenti più recenti prima' },
  { key: 'vecchi', label: 'Movimenti più vecchi prima' },
  { key: 'alti', label: 'Prima gli importi più alti' },
  { key: 'bassi', label: 'Prima gli importi più bassi' },
];
const FILTRI: FiltroDef[] = [
  {
    param: 'side', label: 'Entrate o uscite',
    opzioni: [
      { value: 'CREDIT', label: 'Solo le entrate' },
      { value: 'DEBIT', label: 'Solo le uscite' },
    ],
  },
  {
    param: 'unmatched', label: 'Documento collegato',
    opzioni: [{ value: '1', label: 'Solo i movimenti senza documento' }],
  },
];

export function MovimentiTab({ canWrite, showHistory }: { canWrite: boolean; showHistory?: boolean }) {
  const [accountId, setAccountId] = useUrlState<string>('acc', '');
  const [from, setFrom] = useUrlState<string>('from', '');
  const [to, setTo] = useUrlState<string>('to', '');
  const ordina = useOrdina(ORDINI);
  const filtri = useFiltri(FILTRI);
  const sideParam = filtri.valore('side');
  const side = sideParam === 'DEBIT' || sideParam === 'CREDIT' ? sideParam : '';
  const senzaDocumento = filtri.valore('unmatched') === '1';
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [accountId, from, to, side, senzaDocumento, search, showHistory, ordina.key]);

  // I saldi dei conti restano quelli veri: la data di taglio nasconde solo le righe.
  const { data: accounts } = useQuery(accountsQuery);
  const params = useMemo(() => ({
    accountId: accountId || undefined, from: from || undefined, to: to || undefined,
    side: side as 'DEBIT' | 'CREDIT' | '', senzaDocumento, search: search.trim() || undefined,
    sort: ordina.key, includeHistory: showHistory, page, pageSize: PAGE_SIZE,
  }), [accountId, from, to, side, senzaDocumento, search, ordina.key, showHistory, page]);
  const q = useQuery(movimentiQuery(params));
  const items = q.data?.items ?? [];
  const qontoAccounts = (accounts ?? []).filter((a) => a.provider === 'QONTO');

  return (
    <>
      {/* Sei controlli: su 390 diventavano 218px di barra, la lista partiva
          sotto la piega. Dietro un tasto solo (mobile.md §5). */}
      <MobileFilters attivi={[accountId, from, to, side, senzaDocumento ? '1' : '', search.trim()].filter(Boolean).length} label="Cerca, ordina e filtra">
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        {qontoAccounts.length > 1 && (
          <select className="inp inp-sm" style={{ width: 160 }} value={accountId} onChange={(e) => setAccountId(e.target.value)} aria-label="Conto">
            <option value="">Tutti i conti</option>
            {qontoAccounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
        <input className="inp inp-sm mono" type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="Movimenti dal giorno" title="Movimenti dal giorno" style={{ width: 140 }} />
        <span className="mute text-[12px]">→</span>
        <input className="inp inp-sm mono" type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="Movimenti fino al giorno" title="Movimenti fino al giorno" style={{ width: 140 }} />
        <div className="divider" />
        <div className="tb-search flex-1" style={{ minWidth: 180, margin: 0 }}>
          <Icon name="search" size={12} />
          <input aria-label="Cerca fra i movimenti" placeholder="Cerca un nome o una descrizione…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <span className="mute text-[12px]">{plurale(q.data?.total ?? 0, 'movimento', 'movimenti')}</span>
        <OrdinaFiltra ordina={ordina} filtri={filtri} />
      </div>
      </MobileFilters>

      {q.isLoading && !q.data ? (
        <div className="ed-section"><table className="tbl tbl--ed"><tbody><SkeletonTable rows={8} cols={6} withAvatar={false} /></tbody></table></div>
      ) : items.length === 0 ? (
        <EmptyState
          brand
          title={filtri.attivi > 0 || search.trim() ? 'Nessun movimento del conto con questi filtri' : 'Nessun movimento del conto'}
          description={senzaDocumento
            ? 'In questo periodo ogni movimento è collegato a un documento.'
            : filtri.attivi > 0 || search.trim()
              ? 'Togli un filtro o cambia la ricerca.'
              : 'Collega Qonto e premi Aggiorna banca e fatture: i movimenti arrivano da soli.'}
          action={filtri.attivi > 0 ? <button className="btn" onClick={filtri.clear}>Togli i filtri</button> : undefined}
        />
      ) : (
        <div className="ed-section">
          {/* Scheda sul telefono; la riga non apre niente, il gesto è "Collega". */}
          <table className="tbl tbl--ed tbl--card tbl--static">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Data</th>
                <th>Movimento</th>
                {/* 150 e non 130: "Conto principale" a 130 andava a capo in
                    OGNI riga. 150 è una larghezza che l'app usa già. */}
                {qontoAccounts.length > 1 && <th style={{ width: 150 }}>Conto</th>}
                <th className="num" style={{ width: 130 }}>Importo</th>
                <th style={{ width: 260 }}>Documento collegato</th>
                <th style={{ width: 200 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((t) => <Row key={t.id} t={t} canWrite={canWrite} showAccount={qontoAccounts.length > 1} />)}
            </tbody>
          </table>
          {q.data && <Pagination page={page} pageSize={q.data.pageSize} total={q.data.total} totalPages={q.data.totalPages} onPage={setPage} />}
        </div>
      )}
    </>
  );
}

function Row({ t, canWrite, showAccount }: { t: MovimentoConto; canWrite: boolean; showAccount: boolean }) {
  const { scollegaMovimento } = useBancaActions();
  const [open, setOpen] = useState(false);
  const credit = t.side === 'CREDIT';
  const nome = t.counterparty || t.label;
  const label = `${fmtDate(t.settledOn)}${t.amount != null ? ` · € ${fmtEur(t.amount, true)}` : ''} · ${nome}`;
  return (
    <tr style={{ cursor: 'default' }}>
      <td data-m="key" data-l="Data" className="mono mute" style={{ whiteSpace: 'nowrap' }}>{fmtDate(t.settledOn)}</td>
      <td data-m="hd" className="adm-clip">
        <div className="font-semibold" title={nome}>{nome}</div>
        {t.counterparty && t.label !== t.counterparty && <div className="text-[12px] mute" title={t.label}>{t.label}</div>}
      </td>
      {/* Il conto è lo stesso su quasi tutte le righe: in scheda è rumore. */}
      {showAccount && <td data-m="off" className="mute">{t.accountName ?? '—'}</td>}
      <td data-m="key" data-l="Importo" className={`num mono font-semibold ${credit ? '' : 'mute'}`} style={{ whiteSpace: 'nowrap' }}>
        {t.amount == null
          ? <span className="mute" title="Gli importi degli stipendi li vede solo chi amministra">riservato</span>
          : <>{credit ? '+' : '−'} € {fmtEur(t.amount, true)}</>}
      </td>
      <td data-m="key" data-l="Documento collegato" className="adm-clip">
        <Collegato t={t} />
      </td>
      <td data-m="row" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end gap-1 relative">
          {canWrite && !t.riservato && (t.match ? (
            <button
              className="btn btn-sm btn-ghost"
              disabled={scollegaMovimento.isPending}
              title="Il movimento torna senza documento; la fattura o la voce restano come sono"
              onClick={() => scollegaMovimento.mutate({ transactionId: t.id, label })}
            >
              <Icon name="unlink" size={10} /> Scollega il documento
            </button>
          ) : !t.stipendio && (
            <button className="btn btn-sm" title="Collega a una fattura, una tassa o una spesa ancora da pagare" onClick={() => setOpen((v) => !v)}>
              <Icon name="link" size={10} /> Collega a un documento
            </button>
          ))}
          {open && <CollegaPopover t={t} onClose={() => setOpen(false)} />}
        </div>
      </td>
    </tr>
  );
}

/** Il chip del tipo, e sotto il documento in chiaro. */
function Collegato({ t }: { t: MovimentoConto }) {
  if (t.collegato) {
    return (
      <>
        <div><Chip tone="ok" dot>{COLLEGATO_LABEL[t.collegato.tipo]}</Chip></div>
        {t.collegato.cosa && <div className="text-[12px] mute" title={t.collegato.cosa} style={{ marginTop: 2 }}>{t.collegato.cosa}</div>}
      </>
    );
  }
  if (t.stipendio) {
    const sotto = [t.stipendio.persona, t.stipendio.mese ? `di ${meseInParole(t.stipendio.mese)}` : null].filter(Boolean).join(' ');
    return (
      <>
        <div><Chip tone="info" dot>{t.stipendio.etichetta}</Chip></div>
        {sotto && <div className="text-[12px] mute" style={{ marginTop: 2 }}>{sotto}</div>}
      </>
    );
  }
  return <div className="mute-2 text-[13px]">nessun documento</div>;
}

interface Candidato { key: string; kind: MatchKind | null; source?: UscitaSource; id: string; label: string; amount: number; dueDate: string | null; exact: boolean }

/** "Collega a un documento": entrate → fatture da incassare; uscite → spese e tasse da pagare. Prima quelle con lo stesso importo. */
function CollegaPopover({ t, onClose }: { t: MovimentoConto; onClose: () => void }) {
  const { collegaMovimento, collegaSpesaFissa } = useBancaActions();
  const credit = t.side === 'CREDIT';
  const [choice, setChoice] = useState('');
  const [search, setSearch] = useState('');
  const { data: incassi } = useQuery({ ...incassiQuery({ status: 'aperte', search: search.trim() || undefined, pageSize: 50 }), enabled: credit });
  const { data: uscite } = useQuery({ ...usciteQuery({ status: 'aperte', search: search.trim() || undefined, pageSize: 50 }), enabled: !credit });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const options = useMemo<Candidato[]>(() => {
    const same = (a: number) => t.amount != null && Math.abs(a - t.amount) < 0.005;
    if (credit) {
      return (incassi?.items ?? [])
        .map<Candidato>((r) => ({
          key: `INVOICE_PAYMENT:${r.id}`, kind: 'INVOICE_PAYMENT', id: r.id,
          label: `Fattura ${r.invoiceNumber} di ${r.client.shortName}${r.ofTotal > 1 ? `, rata ${r.seq} di ${r.ofTotal}` : ''}`,
          amount: r.amount, dueDate: r.dueDate, exact: same(r.amount),
        }))
        .sort((a, b) => Number(b.exact) - Number(a.exact));
    }
    return (uscite?.items ?? [])
      .map<Candidato>((r) => ({
        key: `${r.source}:${r.id}:${r.dueDate ?? ''}`, kind: SOURCE_TO_MATCH[r.source], source: r.source, id: r.id,
        label: `${USCITA_LABEL[r.source]}: ${r.counterparty}${r.description ? `, ${r.description}` : ''}${r.source === 'RECURRING' && r.dueDate ? `, ${fmtDate(r.dueDate)}` : ''}`,
        amount: r.amount, dueDate: r.dueDate, exact: same(r.amount),
      }))
      .sort((a, b) => Number(b.exact) - Number(a.exact));
  }, [credit, incassi, uscite, t.amount]);

  const chosen = options.find((o) => o.key === choice);
  const exactCount = options.filter((o) => o.exact).length;

  const confirm = () => {
    if (!chosen) return;
    onClose();
    if (chosen.kind) {
      collegaMovimento.mutate({ transactionId: t.id, kind: chosen.kind, id: chosen.id, label: chosen.label });
    } else {
      // Spesa fissa: l'occorrenza nasce pagata con questo movimento (data del movimento, bonifico).
      // L'importo registrato è quello del movimento (lo decide l'API), il metodo quello di Qonto (carta, addebito, bonifico).
      collegaSpesaFissa.mutate({ id: chosen.id, dueDate: chosen.dueDate, paidDate: t.settledOn ?? '', transactionId: t.id, method: metodoDaOperazione(t.operationType), label: chosen.label });
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      <div className="dropdown adm-pop right-0 mt-1 z-[61]" role="dialog" aria-label="Collega il movimento a un documento">
        <div className="adm-pop-hd">
          <span className="t-micro">Collega questo movimento a</span>
          {t.amount != null && <span className="mono font-semibold">{credit ? '+' : '−'} € {fmtEur(t.amount, true)}</span>}
        </div>
        <div className="adm-pop-grid">
          <label className="field-label">Cerca</label>
          <input className="inp inp-sm" placeholder={credit ? 'Numero di fattura o cliente' : 'Fornitore, spesa o tassa'} value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
          <label className="field-label">{credit ? 'Fattura da incassare' : 'Spesa o tassa da pagare'}</label>
          <select className="inp inp-sm" value={choice} onChange={(e) => setChoice(e.target.value)} size={Math.min(6, Math.max(2, options.length + 1))}>
            <option value="">
              {options.length
                ? `${plurale(options.length, 'possibile', 'possibili')}${exactCount ? `, ${exactCount} con lo stesso importo (✓)` : ''}`
                : credit ? 'Nessuna fattura da incassare' : 'Nessuna spesa o tassa da pagare'}
            </option>
            {options.map((o) => (
              <option key={o.key} value={o.key}>{o.exact ? '✓ ' : ''}€ {fmtEur(o.amount, true)} · {o.label}</option>
            ))}
          </select>
        </div>
        <div className="adm-pop-ft">
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Chiudi senza collegare</button>
          <div className="spacer" />
          <button className="btn btn-sm btn-primary" disabled={!chosen || collegaMovimento.isPending || collegaSpesaFissa.isPending} onClick={confirm}>
            <Icon name="link" size={10} /> Collega il movimento
          </button>
        </div>
      </div>
    </>
  );
}
